import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import type { Label } from '../types';
import { labelColour, liveLabels } from '../types';
import { near } from '../area-match';
import { useAnchoredPopover } from './useAnchoredPopover';

/**
 * THE CARD LABELS FIELD — the Area/Epic combobox made MULTI-select, so a card's
 * labels are set from the board's managed, colourable Label objects instead of a
 * free-text comma box. Before #462 you retyped the name as plain text: a colour
 * set in Settings was honoured only on an exact string match, and a typo quietly
 * made a second, colourless tag. Now selected labels are removable chips (the exact
 * card read-view chip, so edit and read match), typing filters the managed set in
 * the shared .sr-ta popover, and a new label can be made in place.
 *
 * A card still carries label NAMES (issue.labels: string[]); the colour is looked
 * up by name, so no schema changed. Reuses AreaField's open/close + near-match
 * logic, ColourField's swatch + selected-check, and the .label-chip from the card
 * face. Keyboard-first: type filters, ↑↓ move, Enter toggles or creates, Backspace
 * on the empty input removes the last chip, Esc closes the popover only, click-away
 * closes it (the app-wide popover rule).
 */

interface Props {
  /** The label names on this card. */
  value: string[];
  /** The board's labels — the managed set offered in the list. */
  labels: Label[];
  disabled: boolean;
  onChange: (names: string[]) => void;
  /** Make a new label with this name; resolves to its id, or an error to show. */
  onCreate: (name: string) => Promise<{ id: string } | { error: string }>;
}

export default function LabelField({ value, labels, disabled, onChange, onCreate }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLSpanElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const pos = useAnchoredPopover(open, wrap, pop);

  // Close on a click away, like every menu/popover in the app. The popover is
  // portaled outside the field, so let a click inside it (or the field) through.
  // Click-away, or Escape, closes it. Escape is caught in the document CAPTURE
  // phase and stopped there: on a card, the card closes on Escape through a
  // window-level (bubble) listener, so the portaled menu's own React handler
  // never beats it and the whole card would drop out of edit instead of the menu
  // closing. Capturing it first gives the open menu first refusal (see ColourField).
  useEffect(() => {
    if (!open) return;
    const away = (e: globalThis.MouseEvent) => {
      const t = e.target as Node;
      if (wrap.current?.contains(t) || pop.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const has = (name: string) => value.some((v) => v.trim().toLowerCase() === name.trim().toLowerCase());

  const live = liveLabels(labels);
  const needle = q.trim().toLowerCase();
  const matches = live.filter((l) => !needle || l.name.toLowerCase().includes(needle)).slice(0, 8);
  const exactLive = live.some((l) => l.name.toLowerCase().trim() === needle);
  const canCreate = needle.length > 0 && !exactLive;
  const nearLabel = canCreate ? live.find((l) => near(l.name, q)) : undefined;

  useEffect(() => {
    setActive(0);
  }, [q, open]);

  const toggle = (name: string) => {
    if (has(name)) onChange(value.filter((v) => v.trim().toLowerCase() !== name.trim().toLowerCase()));
    else onChange([...value, name]);
    setQ('');
    setErr('');
    input.current?.focus({ preventScroll: true });
  };

  const removeChip = (name: string) => {
    onChange(value.filter((v) => v !== name));
    input.current?.focus({ preventScroll: true });
  };

  const doCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    setErr('');
    const res = await onCreate(q.trim());
    setCreating(false);
    if ('id' in res) {
      if (!has(q)) onChange([...value, q.trim()]);
      setQ('');
      input.current?.focus({ preventScroll: true });
    } else {
      setErr(res.error);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // Swallow it so it closes only the popover, not the card around it.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (matches[active]) toggle(matches[active].name);
      else if (canCreate) void doCreate();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, Math.max(0, matches.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Backspace' && q === '' && value.length) {
      e.preventDefault();
      removeChip(value[value.length - 1]);
    }
  };

  // Clicking the field (but not a chip's ×) focuses the input and opens the list.
  const onFieldMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    if ((e.target as HTMLElement).closest('.label-chip-x')) return;
    if (e.target !== input.current) {
      e.preventDefault();
      input.current?.focus({ preventScroll: true });
    }
    setOpen(true);
  };

  return (
    <span className="area-combo label-combo" ref={wrap}>
      <div
        className={`ctl quiet label-field ${disabled ? 'is-disabled' : ''}`}
        onMouseDown={onFieldMouseDown}
      >
        {value.map((name) => {
          const colour = labelColour(name, labels);
          return (
            <span key={name} className={`epic-chip label-chip${colour ? ' has-colour' : ''}`}>
              {colour && <span className="label-dot" style={{ background: colour }} aria-hidden="true" />}
              {name}
              {!disabled && (
                <button
                  type="button"
                  className="label-chip-x"
                  aria-label={`Remove ${name}`}
                  onClick={() => removeChip(name)}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
        <input
          ref={input}
          className="label-input"
          placeholder="Add a label…"
          aria-label="Labels"
          disabled={disabled}
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setErr('');
          }}
          onKeyDown={onKeyDown}
        />
      </div>

      {open && !disabled && createPortal(
        <div
          ref={pop}
          className="sr-ta area-pop label-pop"
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            maxHeight: pos?.maxH,
            overflowY: 'auto',
            zIndex: 200,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <div className="sr-ta-body">
            {nearLabel && (
              <button type="button" className="sr-ta-opt area-near" onClick={() => toggle(nearLabel.name)}>
                <span
                  className={`epic-sw ${nearLabel.colour ? '' : 'epics-sw-none'}`}
                  style={nearLabel.colour ? { background: nearLabel.colour } : undefined}
                  aria-hidden="true"
                />
                <span className="sr-ta-opt-text">
                  <span className="sr-ta-name">Did you mean {nearLabel.name}?</span>
                  <span className="sr-ta-sub">pick the label you already have</span>
                </span>
              </button>
            )}

            {matches.map((l, i) => {
              const on = has(l.name);
              return (
                <button
                  type="button"
                  key={l.id}
                  className={`sr-ta-opt ${i === active ? 'is-active' : ''}`}
                  aria-pressed={on}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => toggle(l.name)}
                >
                  <span
                    className={`epic-sw ${l.colour ? '' : 'epics-sw-none'}`}
                    style={l.colour ? { background: l.colour } : undefined}
                    aria-hidden="true"
                  />
                  <span className="sr-ta-opt-text"><span className="sr-ta-name">{l.name}</span></span>
                  {on && (
                    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                      <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              );
            })}

            {canCreate && (
              <button type="button" className="sr-ta-opt area-create" onClick={() => void doCreate()} disabled={creating}>
                <span className="area-create-plus" aria-hidden="true">+</span>
                <span className="sr-ta-opt-text">
                  <span className="sr-ta-name">{creating ? 'Creating…' : <>Create “{q.trim()}”</>}</span>
                  <span className="sr-ta-sub">a new label · colour it in Settings</span>
                </span>
              </button>
            )}

            {err && <p className="sr-ta-empty area-err">{err}</p>}
            {matches.length === 0 && !canCreate && (
              <p className="sr-ta-empty">No labels yet — type a name to create the first.</p>
            )}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
